import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { API_BASE_URL } from '../config/api';
import './AdminComponents.css';

const Analytics = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [usageData, setUsageData] = useState(null);
  const [modelData, setModelData] = useState(null);
  const [lambdaData, setLambdaData] = useState(null);
  const [recentConversations, setRecentConversations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('7d');
  const [activeTab, setActiveTab] = useState('usage');
  const [showDeletedConversations, setShowDeletedConversations] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, [timeRange, showDeletedConversations]);

  const getAuthHeaders = () => {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('auth_token='))
      ?.split('=')[1];
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load usage analytics
      const usageResponse = await fetch(
        `${API_BASE_URL}/admin/analytics/usage?timeRange=${timeRange}`,
        { headers: getAuthHeaders() }
      );

      if (!usageResponse.ok) {
        throw new Error(`Failed to load usage analytics: ${usageResponse.statusText}`);
      }

      const usage = await usageResponse.json();
      setUsageData(usage);

      // Load model analytics
      const modelResponse = await fetch(
        `${API_BASE_URL}/admin/analytics/models?timeRange=${timeRange}`,
        { headers: getAuthHeaders() }
      );

      if (!modelResponse.ok) {
        throw new Error(`Failed to load model analytics: ${modelResponse.statusText}`);
      }

      const models = await modelResponse.json();
      setModelData(models);

      // Load Lambda metrics
      const lambdaResponse = await fetch(
        `${API_BASE_URL}/admin/analytics/lambda?timeRange=${timeRange}`,
        { headers: getAuthHeaders() }
      );

      if (!lambdaResponse.ok) {
        const lambdaError = await lambdaResponse.json();
        // Set Lambda data with error information
        setLambdaData({ 
          error: true, 
          errorMessage: lambdaError.message || `Failed to load Lambda metrics: ${lambdaResponse.statusText}`,
          errorDetails: lambdaError.details || null
        });
      } else {
        const lambda = await lambdaResponse.json();
        setLambdaData(lambda);
      }

      // Load recent conversations
      const conversationsResponse = await fetch(
        `${API_BASE_URL}/admin/analytics/recent-conversations?timeRange=${timeRange}&limit=20&includeDeleted=${showDeletedConversations}`,
        { headers: getAuthHeaders() }
      );

      if (!conversationsResponse.ok) {
        console.warn('Could not load recent conversations:', conversationsResponse.statusText);
        setRecentConversations({ conversations: [], totalCount: 0 });
      } else {
        const conversations = await conversationsResponse.json();
        setRecentConversations(conversations);
      }

    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num?.toString() || '0';
  };

  const getTimeRangeLabel = (range) => {
    switch (range) {
      case '1h': return 'Last Hour';
      case '24h': return 'Last 24 Hours';
      case '7d': return 'Last 7 Days';
      case '30d': return 'Last 30 Days';
      default: return 'Last 7 Days';
    }
  };

  const calculatePercentage = (value, total) => {
    if (!total || total === 0) return 0;
    return ((value / total) * 100).toFixed(1);
  };

  const exportData = () => {
    const data = {
      timeRange: getTimeRangeLabel(timeRange),
      generatedAt: new Date().toISOString(),
      usage: usageData,
      models: modelData,
      lambda: lambdaData
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${timeRange}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const navigateToConversation = (conversation) => {
    const includeDeletedParam = conversation.isDeleted ? '?includeDeleted=true' : '';
    navigate(`/admin/users/${conversation.user.userId}/chats/${conversation.conversationId}${includeDeletedParam}`);
  };

  if (loading) {
    return (
      <div className="analytics">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics">
        <div className="error-state">
          <h3>⚠️ Error Loading Analytics</h3>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={loadAnalytics} className="retry-button">
              🔄 Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics">
      <div className="analytics-header">
        <div>
          <h2>📈 Analytics</h2>
          <p>Usage statistics and data visualization for {getTimeRangeLabel(timeRange)}</p>
        </div>
        <div className="analytics-actions">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="time-range-selector"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          <button onClick={exportData} className="export-button">
            📊 Export Data
          </button>
          <button onClick={loadAnalytics} className="refresh-button" disabled={loading}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Analytics Tabs */}
      <div className="analytics-tabs">
        <button
          className={`tab-button ${activeTab === 'usage' ? 'active' : ''}`}
          onClick={() => setActiveTab('usage')}
        >
          📊 Usage Analytics
        </button>
        <button
          className={`tab-button ${activeTab === 'models' ? 'active' : ''}`}
          onClick={() => setActiveTab('models')}
        >
          🤖 Model Usage
        </button>
        <button
          className={`tab-button ${activeTab === 'lambda' ? 'active' : ''}`}
          onClick={() => setActiveTab('lambda')}
        >
          ⚡ Lambda Metrics
        </button>
      </div>

      {/* Usage Analytics Tab */}
      {activeTab === 'usage' && usageData && (
        <div className="analytics-content">
          {/* Usage Overview Cards */}
          <div className="analytics-grid">
            <div className="analytics-card">
              <div className="card-header">
                <span className="card-icon">👥</span>
                <span className="card-title">Active Users</span>
              </div>
              <div className="card-value">{formatNumber(usageData.activeUsers || 0)}</div>
              <div className="card-detail">Users who logged in during this period</div>
            </div>

            <div className="analytics-card">
              <div className="card-header">
                <span className="card-icon">💬</span>
                <span className="card-title">New Conversations</span>
              </div>
              <div className="card-value">{formatNumber(usageData.newConversations || 0)}</div>
              <div className="card-detail">Conversations created during this period</div>
            </div>

            <div className="analytics-card">
              <div className="card-header">
                <span className="card-icon">📝</span>
                <span className="card-title">New Messages</span>
              </div>
              <div className="card-value">{formatNumber(usageData.newMessages || 0)}</div>
              <div className="card-detail">Messages sent during this period</div>
            </div>

            <div className="analytics-card">
              <div className="card-header">
                <span className="card-icon">📈</span>
                <span className="card-title">Avg Messages/Conv</span>
              </div>
              <div className="card-value">
                {usageData.newConversations > 0 
                  ? (usageData.newMessages / usageData.newConversations).toFixed(1)
                  : '0'
                }
              </div>
              <div className="card-detail">Average messages per conversation</div>
            </div>
          </div>

          {/* Usage Trends */}
          <div className="analytics-section">
            <h3>📊 Usage Trends</h3>
            <div className="trends-container">
              {usageData.analyticsData && usageData.analyticsData.length > 0 ? (
                <div className="trends-chart">
                  <div className="chart-placeholder">
                    <div className="chart-bars">
                      {usageData.analyticsData.slice(0, 10).map((metric, index) => (
                        <div key={index} className="chart-bar">
                          <div 
                            className="bar-fill" 
                            style={{ 
                              height: `${Math.min((metric.value / Math.max(...usageData.analyticsData.map(m => m.value))) * 100, 100)}%` 
                            }}
                          ></div>
                          <div className="bar-label">
                            {new Date(metric.timestamp).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="chart-description">
                    Daily usage metrics over the selected time period
                  </p>
                </div>
              ) : (
                <div className="no-trends">
                  <p>📊 No trend data available for this time period</p>
                  <p>Analytics data will appear as users interact with the system</p>
                </div>
              )}
            </div>
          </div>

          {/* Time Period Summary */}
          <div className="analytics-section">
            <h3>📅 Period Summary</h3>
            <div className="summary-grid">
              <div className="summary-item">
                <span className="summary-label">Time Range:</span>
                <span className="summary-value">{getTimeRangeLabel(timeRange)}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Start Date:</span>
                <span className="summary-value">
                  {new Date(usageData.startTime).toLocaleString()}
                </span>
              </div>
              <div className="summary-item">
                <span className="summary-label">End Date:</span>
                <span className="summary-value">
                  {new Date(usageData.endTime).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Recent Conversations */}
          {recentConversations && (
            <div className="analytics-section">
              <div className="section-header">
                <h3>💬 Most Recent Conversations</h3>
                <div className="section-controls">
                  <label className="toggle-container">
                    <input
                      type="checkbox"
                      checked={showDeletedConversations}
                      onChange={(e) => setShowDeletedConversations(e.target.checked)}
                    />
                    <span className="toggle-label">Show deleted conversations</span>
                  </label>
                </div>
              </div>
              <div className="recent-conversations">
                {recentConversations.conversations && recentConversations.conversations.length > 0 ? (
                  <>
                    <div className="conversations-header">
                      <span className="conversations-count">
                        Showing {recentConversations.conversations.length} of {recentConversations.totalCount} conversations
                        {showDeletedConversations && ' (including deleted)'}
                      </span>
                    </div>
                    <div className="conversations-list">
                      {recentConversations.conversations.map((conversation) => (
                        <div 
                          key={conversation.conversationId} 
                          className={`conversation-item ${conversation.isDeleted ? 'deleted' : ''} clickable`}
                          onClick={() => navigateToConversation(conversation)}
                          title="Click to view conversation"
                        >
                          <div className="conversation-main">
                            <div className="conversation-title">
                              <span className="title-text">{conversation.title}</span>
                              <div className="conversation-badges">
                                {conversation.isDeleted && <span className="deleted-badge">🗑️ DELETED</span>}
                                <span className="conversation-id">ID: {conversation.conversationId.slice(0, 8)}...</span>
                              </div>
                            </div>
                            <div className="conversation-user">
                              <span className="user-icon">👤</span>
                              <span className="user-name">{conversation.user.username}</span>
                              <span className="user-email">({conversation.user.email})</span>
                            </div>
                          </div>
                          <div className="conversation-meta">
                            <div className="conversation-stats">
                              <div className="stat-item">
                                <span className="stat-icon">💬</span>
                                <span className="stat-value">{conversation.messageCount}</span>
                                <span className="stat-label">messages</span>
                              </div>
                              <div className="stat-item">
                                <span className="stat-icon">🤖</span>
                                <span className="stat-value">{conversation.lastModel || 'N/A'}</span>
                                <span className="stat-label">model</span>
                              </div>
                            </div>
                            <div className="conversation-timestamps">
                              <div className="timestamp-item">
                                <span className="timestamp-label">Created:</span>
                                <span className="timestamp-value">
                                  {new Date(conversation.createdAt).toLocaleDateString()} {new Date(conversation.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                              <div className="timestamp-item">
                                <span className="timestamp-label">Last Activity:</span>
                                <span className="timestamp-value">
                                  {new Date(conversation.updatedAt).toLocaleDateString()} {new Date(conversation.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                              {conversation.isDeleted && (
                                <div className="timestamp-item">
                                  <span className="timestamp-label">Deleted:</span>
                                  <span className="timestamp-value">
                                    {conversation.deletedAt ? 
                                      `${new Date(conversation.deletedAt).toLocaleDateString()} ${new Date(conversation.deletedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` :
                                      'Unknown'
                                    }
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="no-conversations">
                    <p>💬 No recent conversations found for this time period</p>
                    <p>Conversations will appear here as users interact with the system</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Model Usage Tab */}
      {activeTab === 'models' && modelData && (
        <div className="analytics-content">
          {/* Model Usage Overview */}
          <div className="analytics-grid">
            <div className="analytics-card">
              <div className="card-header">
                <span className="card-icon">🤖</span>
                <span className="card-title">Total Model Calls</span>
              </div>
              <div className="card-value">{formatNumber(modelData.totalMessages || 0)}</div>
              <div className="card-detail">AI model invocations during this period</div>
            </div>

            <div className="analytics-card">
              <div className="card-header">
                <span className="card-icon">🎯</span>
                <span className="card-title">Models Used</span>
              </div>
              <div className="card-value">{modelData.modelStats?.length || 0}</div>
              <div className="card-detail">Different models used during this period</div>
            </div>

            <div className="analytics-card">
              <div className="card-header">
                <span className="card-icon">⭐</span>
                <span className="card-title">Most Popular</span>
              </div>
              <div className="card-value">
                {modelData.modelStats?.[0]?.originalName || 'N/A'}
              </div>
              <div className="card-detail">
                {modelData.modelStats?.[0]?.count || 0} uses
              </div>
            </div>

            <div className="analytics-card">
              <div className="card-header">
                <span className="card-icon">📊</span>
                <span className="card-title">Avg per Model</span>
              </div>
              <div className="card-value">
                {modelData.modelStats?.length > 0 
                  ? (modelData.totalMessages / modelData.modelStats.length).toFixed(1)
                  : '0'
                }
              </div>
              <div className="card-detail">Average usage per model</div>
            </div>
          </div>

          {/* Model Usage Breakdown */}
          <div className="analytics-section">
            <h3>🤖 Model Usage Breakdown</h3>
            <div className="model-stats">
              {modelData.modelStats && modelData.modelStats.length > 0 ? (
                modelData.modelStats.map((model, index) => (
                  <div key={model.model} className="model-stat-item">
                    <div className="model-info">
                      <div className="model-name">{model.originalName || model.model}</div>
                      <div className="model-usage">
                        {formatNumber(model.count)} uses • {calculatePercentage(model.count, modelData.totalMessages)}%
                      </div>
                    </div>
                    <div className="model-bar">
                      <div 
                        className="model-bar-fill"
                        style={{ 
                          width: `${calculatePercentage(model.count, modelData.totalMessages)}%`,
                          backgroundColor: `hsl(${(index * 137.5) % 360}, 70%, 50%)`
                        }}
                      ></div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-model-data">
                  <p>🤖 No model usage data available for this time period</p>
                  <p>Model statistics will appear as users send messages</p>
                </div>
              )}
            </div>
          </div>

          {/* Model Performance Insights */}
          {modelData.modelStats && modelData.modelStats.length > 0 && (
            <div className="analytics-section">
              <h3>💡 Model Insights</h3>
              <div className="insights-grid">
                <div className="insight-card">
                  <div className="insight-icon">🏆</div>
                  <div className="insight-content">
                    <div className="insight-title">Most Popular Model</div>
                    <div className="insight-value">{modelData.modelStats[0].originalName || modelData.modelStats[0].model}</div>
                    <div className="insight-detail">
                      {calculatePercentage(modelData.modelStats[0].count, modelData.totalMessages)}% of all usage
                    </div>
                  </div>
                </div>

                <div className="insight-card">
                  <div className="insight-icon">📈</div>
                  <div className="insight-content">
                    <div className="insight-title">Usage Distribution</div>
                    <div className="insight-value">
                      {modelData.modelStats.length > 1 ? 'Diverse' : 'Concentrated'}
                    </div>
                    <div className="insight-detail">
                      {modelData.modelStats.length} different models used
                    </div>
                  </div>
                </div>

                <div className="insight-card">
                  <div className="insight-icon">⚡</div>
                  <div className="insight-content">
                    <div className="insight-title">Activity Level</div>
                    <div className="insight-value">
                      {modelData.totalMessages > 100 ? 'High' : 
                       modelData.totalMessages > 20 ? 'Medium' : 'Low'}
                    </div>
                    <div className="insight-detail">
                      {formatNumber(modelData.totalMessages)} total model calls
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Lambda Metrics Tab */}
      {activeTab === 'lambda' && lambdaData && (
        <div className="analytics-content">
          {/* Show error message if there are permission issues */}
          {lambdaData.error ? (
            <div className="lambda-error-state">
              <div className="error-card">
                <div className="error-header">
                  <span className="error-icon">🔒</span>
                  <h3>CloudWatch Metrics Access Required</h3>
                </div>
                <div className="error-content">
                  <p className="error-message">{lambdaData.errorMessage}</p>
                  
                  {lambdaData.errorDetails && (
                    <div className="error-details">
                      <h4>Missing Permissions:</h4>
                      <ul>
                        {lambdaData.errorDetails.missingPermissions?.map((permission, index) => (
                          <li key={index}><code>{permission}</code></li>
                        ))}
                      </ul>
                      
                      <h4>Solution:</h4>
                      <p>{lambdaData.errorDetails.solution}</p>
                      
                      <div className="error-actions">
                        <button onClick={loadAnalytics} className="retry-button">
                          🔄 Retry After Permissions Update
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Lambda Performance Overview */}
              <div className="analytics-grid">
            <div className="analytics-card">
              <div className="card-header">
                <span className="card-icon">⚡</span>
                <span className="card-title">Total Invocations</span>
              </div>
              <div className="card-value">{formatNumber(lambdaData.summary?.totalInvocations || 0)}</div>
              <div className="card-detail">Function calls during this period</div>
            </div>

            <div className="analytics-card">
              <div className="card-header">
                <span className="card-icon">⏱️</span>
                <span className="card-title">Avg Duration</span>
              </div>
              <div className="card-value">{lambdaData.summary?.averageDuration || 0}ms</div>
              <div className="card-detail">Average execution time</div>
            </div>

            <div className="analytics-card">
              <div className="card-header">
                <span className="card-icon">🔥</span>
                <span className="card-title">Max Duration</span>
              </div>
              <div className="card-value">{lambdaData.summary?.maxDuration || 0}ms</div>
              <div className="card-detail">Longest execution time</div>
            </div>

            <div className="analytics-card">
              <div className="card-header">
                <span className="card-icon">❄️</span>
                <span className="card-title">Cold Starts</span>
              </div>
              <div className="card-value">{lambdaData.summary?.totalColdStarts || 0}</div>
              <div className="card-detail">{lambdaData.summary?.coldStartRate || 0}% of invocations</div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="analytics-section">
            <h3>📊 Performance Metrics</h3>
            <div className="performance-grid">
              <div className="performance-card">
                <div className="performance-header">
                  <span className="performance-icon">⏱️</span>
                  <span className="performance-title">Execution Time</span>
                </div>
                <div className="performance-stats">
                  <div className="stat-item">
                    <span className="stat-label">Average:</span>
                    <span className="stat-value">{lambdaData.summary?.averageDuration || 0}ms</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Maximum:</span>
                    <span className="stat-value">{lambdaData.summary?.maxDuration || 0}ms</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Minimum:</span>
                    <span className="stat-value">{lambdaData.summary?.minDuration || 0}ms</span>
                  </div>
                </div>
              </div>

              <div className="performance-card">
                <div className="performance-header">
                  <span className="performance-icon">🎯</span>
                  <span className="performance-title">Reliability</span>
                </div>
                <div className="performance-stats">
                  <div className="stat-item">
                    <span className="stat-label">Success Rate:</span>
                    <span className="stat-value">
                      {(100 - (lambdaData.summary?.errorRate || 0)).toFixed(1)}%
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Error Rate:</span>
                    <span className="stat-value">{lambdaData.summary?.errorRate || 0}%</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Total Errors:</span>
                    <span className="stat-value">{lambdaData.summary?.totalErrors || 0}</span>
                  </div>
                </div>
              </div>

              <div className="performance-card">
                <div className="performance-header">
                  <span className="performance-icon">❄️</span>
                  <span className="performance-title">Cold Starts</span>
                </div>
                <div className="performance-stats">
                  <div className="stat-item">
                    <span className="stat-label">Cold Start Rate:</span>
                    <span className="stat-value">{lambdaData.summary?.coldStartRate || 0}%</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Total Cold Starts:</span>
                    <span className="stat-value">{lambdaData.summary?.totalColdStarts || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Warm Invocations:</span>
                    <span className="stat-value">
                      {(lambdaData.summary?.totalInvocations || 0) - (lambdaData.summary?.totalColdStarts || 0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Duration Chart */}
          <div className="analytics-section">
            <h3>📈 Execution Time Trends</h3>
            <div className="trends-container">
              {lambdaData.metrics?.duration?.datapoints && lambdaData.metrics.duration.datapoints.length > 0 ? (
                <div className="trends-chart">
                  <div className="chart-placeholder">
                    <div className="chart-bars">
                      {lambdaData.metrics.duration.datapoints.slice(0, 20).map((datapoint, index) => (
                        <div key={index} className="chart-bar">
                          <div 
                            className="bar-fill duration-bar" 
                            style={{ 
                              height: `${Math.min((datapoint.Average / lambdaData.summary.maxDuration) * 100, 100)}%` 
                            }}
                          ></div>
                          <div className="bar-label">
                            {new Date(datapoint.Timestamp).toLocaleTimeString([], { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="chart-description">
                    Average execution time over the selected time period
                  </p>
                </div>
              ) : (
                <div className="no-trends">
                  <p>📊 No execution time data available for this time period</p>
                  <p>Metrics will appear as the Lambda function is invoked</p>
                </div>
              )}
            </div>
          </div>

          {/* Function Information */}
          <div className="analytics-section">
            <h3>🔧 Function Information</h3>
            <div className="function-info">
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Function Name:</span>
                  <span className="info-value">{lambdaData.functionName}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Time Range:</span>
                  <span className="info-value">{getTimeRangeLabel(timeRange)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Data Period:</span>
                  <span className="info-value">
                    {new Date(lambdaData.startTime).toLocaleString()} - {new Date(lambdaData.endTime).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Insights */}
          <div className="analytics-section">
            <h3>💡 Performance Insights</h3>
            <div className="insights-grid">
              <div className="insight-card">
                <div className="insight-icon">
                  {lambdaData.summary?.averageDuration < 1000 ? '🚀' : 
                   lambdaData.summary?.averageDuration < 3000 ? '⚡' : '🐌'}
                </div>
                <div className="insight-content">
                  <div className="insight-title">Performance Level</div>
                  <div className="insight-value">
                    {lambdaData.summary?.averageDuration < 1000 ? 'Excellent' : 
                     lambdaData.summary?.averageDuration < 3000 ? 'Good' : 'Needs Attention'}
                  </div>
                  <div className="insight-detail">
                    Average response time: {lambdaData.summary?.averageDuration || 0}ms
                  </div>
                </div>
              </div>

              <div className="insight-card">
                <div className="insight-icon">
                  {(lambdaData.summary?.errorRate || 0) < 1 ? '✅' : 
                   (lambdaData.summary?.errorRate || 0) < 5 ? '⚠️' : '❌'}
                </div>
                <div className="insight-content">
                  <div className="insight-title">Reliability Status</div>
                  <div className="insight-value">
                    {(lambdaData.summary?.errorRate || 0) < 1 ? 'Excellent' : 
                     (lambdaData.summary?.errorRate || 0) < 5 ? 'Good' : 'Poor'}
                  </div>
                  <div className="insight-detail">
                    {lambdaData.summary?.errorRate || 0}% error rate
                  </div>
                </div>
              </div>

              <div className="insight-card">
                <div className="insight-icon">
                  {(lambdaData.summary?.coldStartRate || 0) < 5 ? '🔥' : 
                   (lambdaData.summary?.coldStartRate || 0) < 15 ? '❄️' : '🧊'}
                </div>
                <div className="insight-content">
                  <div className="insight-title">Cold Start Impact</div>
                  <div className="insight-value">
                    {(lambdaData.summary?.coldStartRate || 0) < 5 ? 'Low' : 
                     (lambdaData.summary?.coldStartRate || 0) < 15 ? 'Moderate' : 'High'}
                  </div>
                  <div className="insight-detail">
                    {lambdaData.summary?.coldStartRate || 0}% cold start rate
                  </div>
                </div>
              </div>
            </div>
          </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Analytics;